import { Component, EventEmitter, Inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DwButtonCommand, DwButtonConfig, DwDateTimeInputOptions, DwLookupInfo, DwMetaDataService, DwMetaDataServiceToken, DwModalConfig, DwModalHandler, DwModalResult } from '@devwareapps/devware-cap';
import * as moment from 'moment';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import {  CodeValuePermitRequestCableType, CodeValueCCTVTypesOfInstall, CodeValuePermitType,  
          PermitPermitTypeEntity, PermitRequestPermitTypeEntity, PermitEntity, PermitRequestEntity, 
          PermitsMetaDataItemNames, PermitsMetaDataLookups, RequiredCableEntity, AccessControlEntity } from '../../meta-data/permits-meta-data.service';
import { PermitRequestService } from '../../services/permit-request.service';
import { PermitService } from '../../services/permit.service';
import * as _ from 'lodash';

@Component({
  selector: 'app-permit-request-modal',
  templateUrl: './permit-request-modal.component.html',
  styleUrls: ['./permit-request-modal.component.scss']
})
export class PermitRequestModalComponent implements OnInit, DwModalHandler {
  
  permitRequest: PermitRequestEntity;

  // form fields
  assignedTo?: number;
  workFlowStatusId?: number;

  unusedPermitTypes: number[] = [];
  unusedPermitTypeLookupItems: DwLookupInfo[] = [];
  
  // Lookups used to drive form
  workflowStatusLookupId = PermitsMetaDataLookups.PermitStatusAllItems;
  activeUserLookupId = PermitsMetaDataLookups.ETHOSUSERWachterEmployeesActiveEmployees;
  permitTypesLookupId = PermitsMetaDataLookups.CodeValuePermitType;

  alwaysChecked: boolean = true;

  dateOptions: DwDateTimeInputOptions = {
    minDate: {
      isToday: true,
    }
  }
  

  constructor(
    private permitService: PermitService,
    private permitRequestService: PermitRequestService,
    private router: Router,
    @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
  ) { }

  modalConfig: DwModalConfig<any>;
  closeModal: EventEmitter<DwModalResult<any>>;

  ngOnInit(): void {

    // Get permit data from the modal config (this will include the latest changes that might not be saved yet, but better than using an old version of the data)
    this.permitRequest = this.modalConfig.data;

    if (this.permitRequest) {
      this.setupData(this.permitRequest.PermitRequestId);
    }
  }

  /**
   * Handles the modal button actions
   * @param buttonConfig 
   * @returns 
   */
  buttonClick(buttonConfig: DwButtonConfig): Observable<DwModalResult<any>> {
    let modalResult: DwModalResult = {
      cancelled: false,
      buttonCommand: buttonConfig.command,
      closeModal: true,
    };

    switch (buttonConfig.command) {
      case DwButtonCommand.saveAndClose:
        return this.createPermit()
          .pipe(map(permitEntity => {
            if (!permitEntity) {
              modalResult.closeModal = false;
            }

            return modalResult;
          }))


      case DwButtonCommand.cancel:
        modalResult.cancelled = true;
        break;
    }

    return of(modalResult);
  }

  /**
   * saves a new permit with the form data and unsed permits types.  
   * @returns 
   */
  createPermit(): Observable<PermitEntity> {

    if (this.unusedPermitTypes.length == 0) {
      return of(null);
    }

    // Always create a new Permit (if they didn't get all the permit types the first time, we are NOT currently updating the existing permit)
    const newPermit = this.buildPermitFromPermitRequest(this.permitRequest, this.unusedPermitTypes);

    // Map the permit types
    this.mapPermitTypes(newPermit, this.unusedPermitTypes);

    // Save the permit and the permit types
    return this.permitService.createPermit(newPermit);
  }


  /**
   * Load the Permit Request along with the existing permit permit types that exists
   * @param permitRequestId 
   */
  setupData(permitRequestId: number) {

    combineLatest([
      this.permitService.getPermitsTypesFromPermit(permitRequestId),
      this.dwMetaDataService.getLookup(this.permitTypesLookupId)
    ]).subscribe(([permitPermitTypes, permitTypeLookupValues]) => {
      // ensure field is initialized
      this.permitRequest.PermitRequestPermitType = this.permitRequest.PermitRequestPermitType || [];

      const permitRequestPermitTypes = this.permitRequest.PermitRequestPermitType.map(prpt => prpt.PermitTypeCv);
      const existingPermitTypes = permitPermitTypes.map(ppt => ppt.PermitTypeCv);

      this.unusedPermitTypes = permitRequestPermitTypes.filter(permitTypeCv => existingPermitTypes.indexOf(permitTypeCv) == -1);

      // Get the lookup with only the unsed values
      this.unusedPermitTypeLookupItems = permitTypeLookupValues.filter(li => this.unusedPermitTypes.find(permitTypeCv => permitTypeCv.toString() == li.Id.toString()));

      // hosw hide the buttons based on if we have unused permit types
      this.modalConfig.buttons[0].hidden = (this.unusedPermitTypes.length == 0);

    })

  }

  /**
   * Map the permit - permit type associative tables
   * @param permitEntity 
   * @param permitTypeCodeValues 
   */
  private mapPermitTypes(permitEntity: PermitEntity, permitTypeCodeValues: number[]) {

    permitEntity.PermitPermitType = [];

    permitTypeCodeValues.forEach(permitTypeCv => {
      const ppt: PermitPermitTypeEntity = {} as any;
      //ppt.PermitId = permit.PermitId; // Gets mapped from parent on save
      ppt.PermitTypeCv = permitTypeCv;
      ppt._itemName = PermitsMetaDataItemNames.PermitPermitType;

      permitEntity.PermitPermitType.push(ppt);
    });

  }

  /**
   * Builds a permit entity from a permit request and the given permit types 
   * @param permitRequestEntity 
   * @param permitTypeCodeValues 
   * @returns 
   */
  private buildPermitFromPermitRequest(permitRequestEntity: PermitRequestEntity, permitTypeCodeValues: number[]): PermitEntity {

    const permit: PermitEntity = {} as any;
    const isOpportunityBased: Boolean = (permitRequestEntity.PermitRequestJobType == 366)

    // values from modal 
    permit.NoteParentId = permitRequestEntity.NoteParentId;
    permit.NewConstruction = permitRequestEntity.NewConstruction;
    permit.ExistingBuilding = permitRequestEntity.ExistingBuilding;
    permit.Jurisdiction = permitRequestEntity.Jurisdiction;
    permit.DueDate = permitRequestEntity.DueDate;

    permit.AssignedToUser = this.assignedTo;
    permit.PermitStatusWs = this.workFlowStatusId;

    permit._itemName = PermitsMetaDataItemNames.Permit;
    permit.RequestedByUser = permitRequestEntity.RequestedByUserId;
    permit.PermitTeamCv = permitRequestEntity.PermitTeamCv;
    permit.PermitRequestid = permitRequestEntity.PermitRequestId;

    permit.JobId = permitRequestEntity.JobId;
    permit.GCPresent = permitRequestEntity.GCPresent;
    permit.GCName = permitRequestEntity.GCName;

    permit.JobSiteId = permitRequestEntity.JobSiteId;
    permit.SubcontractorLicense = permitRequestEntity.SubContractorLicenseNumber;
    permit.ScopeOfWork = permitRequestEntity.SpecificDescription;
    permit.EstimatedStartDate = permitRequestEntity.ExpectedStartDate;
    permit.EstimatedJobCompletionDate = permitRequestEntity.ExpectedCompletion;
    permit.FacilitySquareFeet = permitRequestEntity.FacilitySquareFeet;
    permit.WorkplaceSquareFeet = permitRequestEntity.WorkplaceSquareFeet;
    permit.FloorCount = permitRequestEntity.FloorCount;
    permit.RoomCount = permitRequestEntity.NumberOfRooms;
    permit.PlenumOrRiserCv = permitRequestEntity.PlenumRiser;
    permit.UndergroundCablingPresent = permitRequestEntity.HasUndergroundCabling;
    permit.CablingInConduitPresent = permitRequestEntity.HasCableInConduit;
    permit.OverallContractValuation = permitRequestEntity.ValuationOverallContract == null ? 0 : permitRequestEntity.ValuationOverallContract;
    permit.OverallSOWValuation = permitRequestEntity.ValuationOverallContract == null ? 0 : permitRequestEntity.ValuationOverallSOW;
    permit.BuildingPermitNumber = permitRequestEntity.GCPermitNumber;
    permit.PropertyOwnerName = null;
    permit.PropertyOwnerAddress = null;
    permit.PropertyOwnerCity = null;
    permit.PropertyOwnerStateId = null;
    permit.PropertyOwnerPostalCode = null;

    // No longer using
    //permit.PermitTypeCv = permitTypeCodeValue;
    permit.DueDate = moment(permitRequestEntity.DueDate).format("MM/DD/YYYY");

    permit.HasCameras = permit.HasCameras;
    permit.HasSpeakers = permitRequestEntity.HasSpeakers;
    permit.SpeakerQuantity = permitRequestEntity.SpeakersQuantity;

    permit.CanBePulledOnline = (permitRequestEntity.PulledOnline == null) ? false : permitRequestEntity.PulledOnline;
    permit.CreatedByDateTime = permitRequestEntity.CreatedByDateTime;
    permit.CreatedByEthos_UserId = permitRequestEntity.CreatedByEthos_UserId;

    permit.PerformingWorkCv = permitRequestEntity.PerformingWorkCv;

    permit.RequiredCable = permitRequestEntity.RequiredCable.map<RequiredCableEntity>(c => { 
      c.PermitRequestId = null; 
      c.RequiredCableId = null;
      return this.hasPermitType(permitTypeCodeValues, c.PermitTypeCv) ? c : null; 
    } );

    // permit.CableTypeCv = permitRequestEntity.CableTypeCv == null ? null : permitRequestEntity.CableTypeCv;
    // permit.CableQuantity = permitRequestEntity.DataCableQuantity;
    if (this.hasPermitType(permitTypeCodeValues, CodeValuePermitType.DataVoice)) {
      // permit.CableTypeCv = permitRequestEntity.VoiceDataCableTypeCV;
      permit.DataVoiceNewCable = permitRequestEntity.DataVoiceNewCable;
      permit.DataVoiceReplacingCable = permitRequestEntity.DataVoiceReplacingCable;
      permit.DataVoiceUsingExisting = permitRequestEntity.DataVoiceUsingExisting;
      permit.PlenumRated = permitRequestEntity.VoiceDataPlenumRated;
      // permit.CableQuantity = permitRequestEntity.VoiceDataQuantity;
      // if (permitRequestEntity.VoiceDataCableTypeCV == CodeValuePermitRequestCableType.Other) {
      //   permit.CableTypeOtherName = permitRequestEntity.DataVoiceOtherCable;
      // }
    }

    if (this.hasPermitType(permitTypeCodeValues, CodeValuePermitType.CCTV)) {
      //CCTV 
      permit.HasCameras = permitRequestEntity.HasCameras;
      // permit.CableTypeCv = permitRequestEntity.CCTVCableTypeCV;
      // permit.CableQuantity = permitRequestEntity.CCTVQuantity;
      permit.CCTVNewCable = permitRequestEntity.CCTVNewCable;
      permit.CCTVReplacingCable = permitRequestEntity.CCTVReplacingCable;
      permit.CCTVUsingExisting = permitRequestEntity.CCTVUsingExisting;
      permit.PlenumRated = permitRequestEntity.CCTVPlenumRated;
      permit.CCTVInstallTypeCv = permitRequestEntity.CCTVInstallTypeCv;
      permit.CameraQuantity = permitRequestEntity.CamerasQuantity;

      // if (permitRequestEntity.CCTVCableTypeCV == CodeValuePermitRequestCableType.Other) {
      //   permit.CableTypeOtherName = permitRequestEntity.CCTVOtherCable;
      // }

      if (permitRequestEntity.CCTVInstallTypeCv == CodeValueCCTVTypesOfInstall.Other) {
        permit.CCTVOtherInstallType = permitRequestEntity.CCTVOtherInstallType;
      }
    }

    if (this.hasPermitType(permitTypeCodeValues, CodeValuePermitType.AudioVisual)) {
      // permit.CableTypeCv = permitRequestEntity.AudioCableTypeCV;
      // permit.CableQuantity = permitRequestEntity.AudioQuantity;
      permit.PlenumRated = permitRequestEntity.AudioPlenumRated;
      // if (permitRequestEntity.VoiceDataCableTypeCV == CodeValuePermitRequestCableType.Other) {
      //   permit.CableTypeOtherName = permitRequestEntity.AudioVisualOtherCable;
      // }
    }

    if (this.hasPermitType(permitTypeCodeValues, CodeValuePermitType.FireAlarm)) {
      permit.InitiationDeviceQuantity = permitRequestEntity.FireAlarmInitiatingDevices;
      permit.FireAlarmAdditionalDevices = permitRequestEntity.NumberAditionalDevices;
      permit.NACQuantity = permitRequestEntity.NumberNaNC;
      permit.SwappingOutPanel = permitRequestEntity.IsSwappingOutPanel;
      permit.IsNewFireSystem = permitRequestEntity.IsNewFireSystem;
      permit.FireAlarmDemoInstallCv = permitRequestEntity.FireAlarmDemoInstallCv;
      permit.FireAlarmInstallRadio = permitRequestEntity.FireAlarmInstallRadio;
    }

    if (this.hasPermitType(permitTypeCodeValues, CodeValuePermitType.Electrical)) {
      permit.OutletQuantity = permitRequestEntity.OutletQuantity;
      permit.ElectricalPanelQuantity = permitRequestEntity.PanelsQuantity
      permit.CircuitQuantity = permitRequestEntity.CircuitsQuantity;
      permit.ElectricalOutletTypeCv = permitRequestEntity.ElectricalOutletTypeCv;
      permit.ElectricalOtherOutletType = permitRequestEntity.ElectricalOtherOutletType;
      permit.ElectricalWorkTypeCv = permitRequestEntity.ElectricalWorkTypeCv;
      permit.ElectricalOtherDifferentType = permitRequestEntity.ElectricalOtherDifferentType;
      permit.ElectricalOtherWorkType = permitRequestEntity.ElectricalOtherWorkType;
      permit.ElectricalDifferentTypeCv = permitRequestEntity.ElectricalDifferentTypeCv;
      permit.IsLicensedTechnician = permitRequestEntity.IsLicensedTechnician;
      permit.TechnicianNameAndLicenseNumber = permitRequestEntity.TechnicianNameAndLicenseNumber;
      permit.ElectricalTotalVoltage = permitRequestEntity.ElectricalTotalVoltage;
      permit.TotalAmps = permitRequestEntity.TotalAmps;
    }

    permit.AccessControl = permitRequestEntity.AccessControl.map<AccessControlEntity>(c => { 
      c.PermitRequestId = null; 
      c.AccessControlId = null;
      return c; 
    } );

    //permit.AccessControl = permitRequestEntity.AccessControl.map<AccessControlEntity>(c => { c.PermitRequestId = null; return c; } );
    if (this.hasPermitType(permitTypeCodeValues, CodeValuePermitType.AccessControl)) {
      permit.AccessControlTypeCv = (permitRequestEntity.AccessControlCV == null) ? null : permitRequestEntity.AccessControlCV;
      permit.AccessControlOther = permitRequestEntity.AccessControlOther;
      permit.AccessControlQuantity = permitRequestEntity.AccessControlQuantity;
      permit.HasCardReaders = permitRequestEntity.CardReader;
      permit.CardReaderQuantity = permitRequestEntity.CardReaderQuantity;
      permit.HasPaging = permitRequestEntity.Paging;
      permit.IsTyingIntoFireAlarm = permitRequestEntity.IsTyingIntoFireAlarm;
    }

    if (this.hasPermitType(permitTypeCodeValues, CodeValuePermitType.BurglarAlarm)) {
      permit.AlarmPanelInstallation = permitRequestEntity.HasPanelInstalation;
      permit.AlarmPanelQuantity = (permitRequestEntity.BuglarAlarmPanelQuantity == null) ? null : permitRequestEntity.BuglarAlarmPanelQuantity.toString();
      permit.DeviceType = permitRequestEntity.BuglarAlarmDeviceType;
    }

    return permit;
  }


  private hasPermitType(permitTypeCodeValues: number[], permitType: CodeValuePermitType) {
    return permitTypeCodeValues.indexOf(permitType) != -1;
  }

}
